/**
 * Auto-fill script of the guided demo tour: one action per coach mark of the
 * create-model walkthrough, keyed by step id.
 *
 * In demo mode the runner intercepts the click (or key press) on the
 * highlighted element, or on Skip, runs the step's action, then completes
 * the mark. The action fills in what a real user would have typed or picked,
 * through the imperative APIs the wizard and its pages publish in the
 * `WizardApiProvider` registry (`wizardApi.js`): `wizard` (form patch,
 * navigation), `dataCollection`, `modelSetup` / `modelSetupStep`,
 * `dataEditor`.
 *
 * Marks that need no input (a hint over an element, a default we keep) have
 * an action that does nothing — the mark just moves on. There are no "Next"
 * marks: when the last mark of a page completes, the runner reports it is
 * waiting for the next page and `DemoTourRunner` turns the page itself
 * (`DEMO_PAGE_TRANSITIONS`) behind a short spinner on the Next button.
 */
import { TaskType } from '@/shared/domain/taskTypes';
import { LLM_CREATED_DATA_CREATION } from '@/features/datasets/utils/aiGeneratedDatasetDefaults';
import { AUTONOMY_LEVEL_NO_TOOL_CALLING } from '@/features/tasks/components/CreationWizard/businessGoals/autonomyLevels';
import { DEFAULT_PILLAR_ORDER } from '@/features/tasks/components/CreationWizard/businessGoals/constraintOptions';
import {
    DEMO_BENCHMARK_FILE_NAME,
    DEMO_BENCHMARK_ROWS,
    DEMO_DATASET_FILE_NAME,
    DEMO_DATASET_ROWS,
    DEMO_EXAMPLE_COLUMN,
    DEMO_FALLBACK_LABEL,
    DEMO_FLIPPED_LABEL,
    DEMO_LABELS,
    DEMO_OUTPUT_SCHEMA_TEXT,
    DEMO_SYSTEM_PROMPT,
    DEMO_TASK_DESCRIPTION,
    DEMO_TASK_NAME,
    DEMO_TEACHER_MODEL,
    DEMO_TEACHER_PROVIDER,
    DEMO_USER_PROMPT,
    demoRowsToCsv,
} from './demoFixtures';
import { WIZARD_API } from './wizardApi';

/** The classification-type value Business Goals stores for "No" (one label per example). */
export const DEMO_CLASSIFICATION_TYPE = 'single';

/**
 * Pillar order the "Define & Prioritize Business Goals" mark fills in when
 * the user skips instead of dragging: Cost moved right before Quality
 * (`DEFAULT_PILLAR_ORDER` is digital footprint, cost, performance, quality).
 */
export const DEMO_PRIORITY_ORDER = Object.freeze(
    (() => {
        const order = DEFAULT_PILLAR_ORDER.filter((id) => id !== 'cost');
        order.splice(order.indexOf('quality'), 0, 'cost');
        return order;
    })()
);

const csvFile = (rows, name) => new File([demoRowsToCsv(rows)], name, { type: 'text/csv' });

/** The two files the "Upload your data" mark drops in: dataset + benchmark. */
export const demoUploadFiles = () => ({
    dataset: [csvFile(DEMO_DATASET_ROWS, DEMO_DATASET_FILE_NAME)],
    benchmark: [csvFile(DEMO_BENCHMARK_ROWS, DEMO_BENCHMARK_FILE_NAME)],
});

const noop = () => {};

/**
 * step id → `(api) => void | Promise`, where `api(name)` returns the page API
 * registered under `name` (or null when that page is not mounted).
 */
export const DEMO_SCRIPT = Object.freeze({
    // ---- Business goals -------------------------------------------------
    'task-name': (api) =>
        api(WIZARD_API.wizard)?.setForm({ taskName: DEMO_TASK_NAME, description: DEMO_TASK_DESCRIPTION }),
    // Picking a type resets the autonomy level in the real form, so the
    // demo's level 1 ("Model has no tool calling") is set here with it, and
    // "Can one example have more than one label?" is answered No (the tour
    // has no mark for that question).
    'task-type': (api) =>
        api(WIZARD_API.wizard)?.setForm({
            taskType: TaskType.classification,
            autonomyLevel: AUTONOMY_LEVEL_NO_TOOL_CALLING,
            classificationType: DEMO_CLASSIFICATION_TYPE,
        }),
    labels: (api) => api(WIZARD_API.wizard)?.setForm({ labels: [...DEMO_LABELS] }),
    'fallback-label': (api) =>
        api(WIZARD_API.wizard)?.setForm({ fallbackLabel: DEMO_FALLBACK_LABEL, fallbackLabelUserSet: true }),
    // Normally completed by the user dragging Cost next to Quality; the fill
    // (Skip) does the same move.
    'priority-ranking': (api) => api(WIZARD_API.wizard)?.setForm({ priorityOrder: [...DEMO_PRIORITY_ORDER] }),

    // ---- Data collection ------------------------------------------------
    // Drops both IMDb files in and picks the input column (`review` is not
    // one of the names the panel defaults by itself).
    'upload-data': async (api) => {
        const dc = api(WIZARD_API.dataCollection);
        if (!dc) return;
        const files = demoUploadFiles();
        await Promise.all([dc.pickFiles('dataset', files.dataset), dc.pickFiles('benchmark', files.benchmark)]);
        const current = api(WIZARD_API.dataCollection);
        current?.patchDetails(current.primaryTarget, { exampleColumn: DEMO_EXAMPLE_COLUMN });
    },
    'data-creation': (api) => {
        const dc = api(WIZARD_API.dataCollection);
        dc?.patchDetails(dc.primaryTarget, { dataCreation: LLM_CREATED_DATA_CREATION });
    },
    'example-preview': noop,

    // ---- Model setup ----------------------------------------------------
    'commercial-model': (api) =>
        api(WIZARD_API.modelSetup)?.selectTeacher({ provider: DEMO_TEACHER_PROVIDER, model: DEMO_TEACHER_MODEL }),
    'system-prompt': (api) => api(WIZARD_API.modelSetup)?.setSystemPrompt(DEMO_SYSTEM_PROMPT),
    'user-prompt': (api) => api(WIZARD_API.modelSetup)?.setUserPrompt(DEMO_USER_PROMPT),
    // The demo classifier answers `{ "result": ... }`: structured output on.
    'output-format': (api) => api(WIZARD_API.modelSetup)?.setOutputFormat(DEMO_OUTPUT_SCHEMA_TEXT),

    // ---- Data editor ----------------------------------------------------
    'label-view': noop,
    'row-label': (api) => api(WIZARD_API.dataEditor)?.flipLabel(DEMO_FLIPPED_LABEL),
    reviewed: (api) => api(WIZARD_API.dataEditor)?.review(),

    // ---- Train ----------------------------------------------------------
    // No marks: Advanced opens by itself and the user clicks "Finish &
    // Create Model" (answered by the demo backend).
});

/** Step ids the script handles — the whole demo walkthrough. */
export const DEMO_SCRIPT_STEP_IDS = Object.freeze(Object.keys(DEMO_SCRIPT));

/**
 * wizard page → `(api) => void | Promise` that turns to the next page, the
 * way the page's own Next button would. Run by `DemoTourRunner` once the
 * last mark of the page is done.
 */
export const DEMO_PAGE_TRANSITIONS = Object.freeze({
    'business-goals': (api) => api(WIZARD_API.wizard)?.createUsecase(),
    'data-collection': (api) => api(WIZARD_API.dataCollection)?.next(),
    // Finish & Train: the step's Next opens Review Training Plan.
    'model-setup': (api) => api(WIZARD_API.modelSetupStep)?.next(),
});

const apiFor = (registry) => (name) => registry?.get?.(name) ?? null;

const runDemoAction = async (action, label, registry) => {
    if (!action) return;
    try {
        await action(apiFor(registry));
    } catch (error) {
        if (import.meta.env?.DEV) console.warn(`[demo tour] "${label}" failed`, error);
    }
};

/**
 * Run the auto-fill action of `stepId` against `registry` (`{ get(name) }`,
 * see `WizardApiProvider`). Unknown ids are a no-op. Always resolves — a
 * failing action is logged in dev and must not stall the tour.
 */
export async function applyDemoStep(stepId, registry) {
    await runDemoAction(DEMO_SCRIPT[stepId], stepId, registry);
}

/**
 * Turn `page` to the next wizard page (`DEMO_PAGE_TRANSITIONS`). Unknown
 * pages are a no-op; always resolves, like `applyDemoStep`.
 */
export async function applyDemoPageTransition(page, registry) {
    await runDemoAction(DEMO_PAGE_TRANSITIONS[page], `${page} → next page`, registry);
}
